// components/source/sourceTableSlice.ts
// Pure helper that turns a set of Source inventory rows (shortlist fits, campaigns,
// contacts, or a single brand fiche) into a formatted table slice (header + rows +
// per-column alignment). Shared by SOURCE_SHOW_IN_CHAT (which embeds the snapshot in
// the server-durable chat marker) and by ChatSourceTableCard (which re-reads the live
// rows from the kernel inventory), so the durable snapshot renders identically to the
// live card. Unlike the Analyst slice there is NO formula computation: Source data is
// plain server entities read straight off the inventory registries.
//
// Shortlist/contacts expose the FULL column set the real editor shows; the caller can
// pass `columns` (a subset of column keys or headers) to keep/drop columns. Brand and
// contact name cells carry a logo/avatar so they render like the editor.
import { type TableSlice, type SliceCell, cellText, isNumericSliceText } from '@/components/tables/analystTableSlice';

export type SourceCardKind = 'shortlist' | 'campaigns' | 'contacts' | 'brand';
export type SourceCardScope = 'portfolio' | 'asset' | 'unit';

const rec = (x: unknown): Record<string, unknown> =>
  x !== null && typeof x === 'object' ? (x as Record<string, unknown>) : {};
const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const numOf = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const firstStr = (r: Record<string, unknown>, keys: string[]): string => {
  for (const k of keys) {
    const s = str(r[k]).trim();
    if (s) return s;
  }
  return '';
};
const firstNum = (r: Record<string, unknown>, keys: string[]): string => {
  for (const k of keys) {
    const n = numOf(r[k]);
    if (n != null) return n.toLocaleString('en-US');
  }
  return '';
};
const fmtScore = (v: unknown): string => {
  const n = numOf(v);
  return n == null ? '' : String(Math.round(n));
};
const fmtDate = (v: unknown): string => {
  const s = str(v).trim();
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};
// Compact relative time since an ISO timestamp ("3d", "5h", "2mo").
const relTime = (v: unknown): string => {
  const s = str(v).trim();
  if (!s) return '';
  const t = new Date(s).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 0) return '';
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  if (d < 30) return `${Math.floor(d / 7)}w`;
  if (d < 365) return `${Math.floor(d / 30)}mo`;
  return `${Math.floor(d / 365)}y`;
};
// Sentence-case a SCREAMING_SNAKE value ('MASS_MARKET' -> 'Mass market').
const label = (v: unknown): string => {
  const s = str(v).trim();
  if (!s) return '';
  const words = s.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
const boolLabel = (v: unknown): string => (v === true ? 'Yes' : v === false ? 'No' : '');

interface ColDef {
  key: string;
  header: string;
  /** Long free-text column: rendered with a capped width + line clamp (Signal, Comment). */
  long?: boolean;
  cell: (r: Record<string, unknown>) => SliceCell;
}

// The full shortlist column set, matching the real PortfolioFits/Leads/UnitFits tables:
// scope container, brand (with logo), fit score, status, signal/rationale, time since
// scoring, and the manual prospection-tracking fields.
function shortlistColumns(scope: SourceCardScope | undefined): ColDef[] {
  const brandCell: (r: Record<string, unknown>) => SliceCell =
    scope === 'unit'
      ? (r) => ({ text: firstStr(r, ['leadBrandName']) || '(brand)', logoUrl: firstStr(r, ['leadBrandLogoUrl']) || undefined })
      : (r) => ({ text: firstStr(r, ['brandName']) || '(brand)', logoUrl: firstStr(r, ['brandLogoUrl']) || undefined });
  const scoreCell: (r: Record<string, unknown>) => SliceCell =
    scope === 'asset' ? (r) => fmtScore(r.fitScore) : (r) => fmtScore(r.score);
  const scopeCol: ColDef =
    scope === 'portfolio'
      ? { key: 'portfolio', header: 'Portfolio', cell: (r) => firstStr(r, ['portfolioName']) }
      : scope === 'unit'
        ? { key: 'unit', header: 'Unit', cell: (r) => firstStr(r, ['unitIdentifier', 'unitName']) }
        : { key: 'property', header: 'Property', cell: (r) => firstStr(r, ['propertyName']) };
  return [
    scopeCol,
    { key: 'brand', header: 'Brand', cell: brandCell },
    { key: 'fit', header: 'Fit', cell: scoreCell },
    { key: 'status', header: 'Status', cell: (r) => label(r.status) },
    { key: 'signal', header: 'Signal', long: true, cell: (r) => str(r.rationale) },
    { key: 'evaluated', header: 'Evaluated', cell: (r) => relTime(r.updatedAt) },
    { key: 'contacted', header: 'Contacted', cell: (r) => boolLabel(r.prospectionContacted) },
    { key: 'channel', header: 'Channel', cell: (r) => label(r.prospectionChannel) },
    { key: 'response', header: 'Response', cell: (r) => label(r.prospectionResponse) },
    { key: 'comment', header: 'Comment', long: true, cell: (r) => str(r.prospectionComment) },
  ];
}

function contactColumns(): ColDef[] {
  return [
    {
      key: 'name',
      header: 'Name',
      cell: (r) => ({
        text: firstStr(r, ['fullName']) || `${str(r.firstName)} ${str(r.lastName)}`.trim() || '(contact)',
        logoUrl: firstStr(r, ['photoUrl']) || undefined,
      }),
    },
    { key: 'title', header: 'Title', cell: (r) => str(r.title) },
    { key: 'seniority', header: 'Seniority', cell: (r) => label(r.seniority) },
    { key: 'email', header: 'Email', cell: (r) => str(r.email) },
    { key: 'phone', header: 'Phone', cell: (r) => firstStr(r, ['primaryPhone', 'directPhone']) },
    { key: 'linkedin', header: 'LinkedIn', cell: (r) => str(r.linkedinUrl) },
  ];
}

// The run's own name, stamped into its config at launch and editable from the dashboard card.
const configName = (r: Record<string, unknown>): string => {
  const cfg = r.config;
  return cfg && typeof cfg === 'object' ? firstStr(cfg as Record<string, unknown>, ['name']) : '';
};

// A unified Campaign row names its target in `targetName` and reports its own result family in
// `resultCount`; the three legacy rows carried a per-table name and a per-table count. Both are
// read, because a card written before the unified list re-reads its rows from the registry the
// marker recorded, which is still one of the old ones.
function campaignColumns(scope: SourceCardScope | undefined): ColDef[] {
  const scopeLabel = scope === 'portfolio' ? 'Portfolio' : scope === 'unit' ? 'Unit' : 'Asset';
  return [
    {
      key: 'campaign',
      header: 'Campaign',
      cell: (r) => configName(r) || firstStr(r, ['targetName', 'propertyName', 'portfolioName', 'name']) || '(campaign)',
    },
    { key: 'scope', header: 'Scope', cell: () => scopeLabel },
    { key: 'status', header: 'Status', cell: (r) => label(r.status) },
    {
      key: 'fits',
      header: 'Fits',
      cell: (r) =>
        firstNum(r, [
          'resultCount',
          'nonDiscardedLeadsCount',
          'leadsCount',
          'portfolioFitsCount',
          'count',
        ]),
    },
    { key: 'created', header: 'Created', cell: (r) => fmtDate(r.createdAt) },
  ];
}

// Raw keys the curated shortlist columns already consume, plus opaque ids / heavy
// nested fields, so the "all remaining fields" pass does not double them up or dump
// noise. Everything else scalar becomes its own column so no field is missing.
const SHORTLIST_COVERED = new Set([
  'brandName', 'leadBrandName', 'brandLogoUrl', 'leadBrandLogoUrl', 'score', 'fitScore', 'leadFitScore',
  'status', 'rationale', 'updatedAt', 'prospectionContacted', 'prospectionChannel', 'prospectionResponse',
  'prospectionComment', 'portfolioName', 'propertyName', 'unitName', 'unitIdentifier',
]);
const SHORTLIST_SKIP = new Set([
  'id', 'organisationId', 'brandId', 'portfolioId', 'propertyId', 'unitId', 'leadId', 'campaignId',
  'lastCampaignId', 'campaignIds', 'portfolioFitId', 'promotedDealId', 'promotedDealUnitId', 'promotedDealAssignedUserId',
  'createdAt', 'nearestStoreLat', 'nearestStoreLng', 'leadIds',
]);
const humanize = (k: string): string =>
  k
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
// A compact one-line summary of the scoring breakdown array ("Surface: 9; Location: 8").
const summarizeCriteria = (v: unknown): string => {
  if (!Array.isArray(v)) return '';
  return v
    .map((c) => {
      const r = rec(c);
      const name = firstStr(r, ['label', 'name', 'criterion', 'criterionName', 'title', 'key']);
      const sc = firstNum(r, ['score', 'value', 'rating']);
      return name ? (sc ? `${name}: ${sc}` : name) : '';
    })
    .filter(Boolean)
    .join('; ');
};
const isScalar = (v: unknown): boolean =>
  typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

// Build columns for EVERY remaining field on the shortlist rows (the "full raw schema"):
// a Scoring column from criteriaResults if present, then one column per extra scalar key.
function extraShortlistColumns(rows: readonly unknown[]): ColDef[] {
  const cols: ColDef[] = [];
  const hasCriteria = rows.some((r) => Array.isArray(rec(r).criteriaResults) && (rec(r).criteriaResults as unknown[]).length > 0);
  if (hasCriteria) cols.push({ key: 'scoring', header: 'Scoring', long: true, cell: (r) => summarizeCriteria(r.criteriaResults) });
  const seen = new Set<string>();
  for (const r of rows) {
    for (const [k, v] of Object.entries(rec(r))) {
      if (seen.has(k) || SHORTLIST_COVERED.has(k) || SHORTLIST_SKIP.has(k) || k.startsWith('_')) continue;
      if (!isScalar(v) || str(v).trim() === '') continue;
      seen.add(k);
      const long = str(v).length > 40;
      cols.push({
        key: k.toLowerCase(),
        header: humanize(k),
        long,
        cell: (row) => (typeof row[k] === 'boolean' ? boolLabel(row[k]) : typeof row[k] === 'number' ? firstNum(row, [k]) : str(row[k])),
      });
    }
  }
  return cols;
}

// Keep only the requested columns (matched by key OR header, case-insensitive), in the
// requested order. Empty / no request => all columns.
function selectColumns(all: ColDef[], requested: readonly string[] | undefined): ColDef[] {
  if (!requested || requested.length === 0) return all;
  const wanted = requested.map((c) => c.trim().toLowerCase()).filter(Boolean);
  const picked: ColDef[] = [];
  for (const w of wanted) {
    const col = all.find((c) => c.key.toLowerCase() === w || c.header.toLowerCase() === w);
    if (col && !picked.includes(col)) picked.push(col);
  }
  return picked.length > 0 ? picked : all;
}

function sliceFromColumns(
  cols: ColDef[],
  rows: readonly unknown[],
  sheetName: string,
  autoTrimEmpty: boolean
): TableSlice {
  let activeCols = cols;
  // Default view (no explicit column pick): drop columns that are empty for every row
  // so mostly-blank fields (an un-prospected shortlist's channel/response/comment) do
  // not clutter the card. An explicit `columns` request is always honored verbatim.
  if (autoTrimEmpty && rows.length > 0) {
    const nonEmpty = cols.filter((c) => rows.some((r) => cellText(c.cell(rec(r))) !== ''));
    if (nonEmpty.length > 0) activeCols = nonEmpty;
  }
  const header = activeCols.map((c) => c.header);
  const body: SliceCell[][] = rows.map((r) => activeCols.map((c) => c.cell(rec(r))));
  const colAlign: ('left' | 'right')[] = header.map((_, ci) => {
    const vals = body.map((r) => cellText(r[ci] ?? '')).filter((x) => x !== '');
    const numeric = vals.filter((x) => isNumericSliceText(x)).length;
    return vals.length > 0 && numeric >= vals.length / 2 ? 'right' : 'left';
  });
  const colWrap = activeCols.map((c) => !!c.long);
  return { header, rows: body, colAlign, colWrap, sheetName };
}

export function buildSourceSlice(
  kind: SourceCardKind,
  scope: SourceCardScope | undefined,
  rows: readonly unknown[],
  columns?: readonly string[]
): TableSlice | null {
  if (kind === 'brand') {
    if (rows.length === 0) return null;
    const b = rec(rows[0]);
    const countries = Array.isArray(b.operatingCountryCodes)
      ? (b.operatingCountryCodes as unknown[]).map(str).filter(Boolean).join(', ')
      : str(b.geographicFootprint);
    const contactCount = Array.isArray(b.contactIds) ? (b.contactIds as unknown[]).length : 0;
    // The CNCC activity is the retail referential the leasing teams actually speak, and it is what
    // the panel labels "Category (CNCC)". `category` is the coarse internal enum; showing only that
    // meant the card and the agent both missed the line the user was reading.
    const cnccLabel = [str(b.cnccActivity), str(b.cnccSector)].filter(Boolean).join(' · ');
    // A few real store cities, not just a count. "22 stores" cannot answer "where are they?", and
    // the Stores tab beside this is a list of places.
    const cities = Array.isArray(b.locations)
      ? Array.from(
          new Set(
            (b.locations as unknown[])
              .map((loc) => str(rec(loc).city))
              .filter(Boolean),
          ),
        )
      : [];
    const citySample = cities.length > 0
      ? `${cities.slice(0, 8).join(', ')}${cities.length > 8 ? `, +${cities.length - 8} more` : ''}`
      : '';
    const kv: [string, string][] = [
      ['Name', firstStr(b, ['name'])],
      ['Category', label(b.category)],
      ['Category (CNCC)', cnccLabel],
      ['Positioning', str(b.positioningNote)],
      ['Website', str(b.websiteUrl)],
      ['Network size', firstNum(b, ['declaredNetworkSize', 'locationCount'])],
      ['Countries', countries],
      ['Store cities', citySample],
      ['Revenue', firstNum(b, ['sortableTotalRevenue'])],
      ['Contacts', contactCount > 0 ? String(contactCount) : ''],
      // Presence, not the URL: the panel says "Logo: Present/Missing" and that is the whole
      // question. Enough to answer it without guessing, and one asset link less in the prompt.
      ['Logo', str(b.logoUrl) ? 'Present' : 'Missing'],
    ];
    const body: SliceCell[][] = kv.filter(([, v]) => v !== '').map(([kLabel, v]) => [kLabel, v]);
    if (body.length === 0) return null;
    return { header: ['Field', 'Value'], rows: body, colAlign: ['left', 'left'], colWrap: [false, true], sheetName: firstStr(b, ['name']) || 'Brand' };
  }

  const autoTrim = !columns || columns.length === 0;
  if (kind === 'contacts') return sliceFromColumns(selectColumns(contactColumns(), columns), rows, 'contacts', autoTrim);
  if (kind === 'campaigns') return sliceFromColumns(selectColumns(campaignColumns(scope), columns), rows, 'campaigns', autoTrim);
  // Shortlist: curated columns first, then EVERY remaining scalar field + a scoring
  // breakdown, so nothing from the raw schema is missing. Auto-trim drops empties.
  const shortlistCols = [...shortlistColumns(scope), ...extraShortlistColumns(rows)];
  return sliceFromColumns(selectColumns(shortlistCols, columns), rows, 'shortlist', autoTrim);
}
